import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { withNamespaces, Trans } from 'react-i18next';

import { Link } from 'core/routing';

import { markdown } from 'libs/editor/serializers/markdown';
import { EditorStore } from 'libs/editor/store';
import { Editor } from 'libs/editor';
import { TextField } from 'libs/forms/views/fieldTypes/text';
import { EditorField } from 'libs/forms/views/fieldTypes/editor';

import DocumentHead from 'legacyComponents/documentHead';
import { Date } from 'apps/components/date';
import {
  Div,
  SubPageTitle,
  SubPageDescription,
  IssueWrapper,
  SubPageWrapper,
  SubPageStatsWrapper,
  SubPageStatName,
  Tag,
} from 'legacyComponents/styled/lib';

import { clientStore } from 'apps/client/app';
import { modalStore } from 'apps/modals/app';
import { ConfirmAction } from 'apps/modals/confirmAction';
import { Comments } from 'apps/comments/main';
import { imageSrc } from 'apps/file/utils';
import {
  TitleInput,
  MsgInput,
  Edit,
  DeleteBtn,
} from 'apps/aggregator/views/postPageContent';
import { RepoSpinner } from 'apps/repository/styles';
import { FourOhFour } from 'apps/fourOhFour/index';
import { conf as mainConf } from 'apps/config.yaml';

import { ThreadPageInfo, CategoryName } from 'apps/forum/styles';
import { ThreadAppManager } from 'apps/forum/thread/appManager';
import { Intro } from 'apps/forum/views/intro';
import Like from 'apps/content/views/like';

import {
  AuthorAvatar,
  FullName,
  UsernameWrapper,
  ButtonStyled,
  RowWrapper,
  ColumnWrapper,
} from 'apps/forum/thread/body.styles';

@withNamespaces()
@observer
export class ThreadBody extends React.Component {
  constructor(props) {
    super(props);
    this.appManager = new ThreadAppManager(this, this.props);
  }

  confirmDelete() {
    const {
      threadPage: { Delete },
    } = this.props;
    modalStore.SetModalComponent(ConfirmAction, {
      callBack: Delete,
      title: 'delete this Thread?',
      undo: false,
    });
  }

  render() {
    const { preview } = this.props;
    const { threadPage, loading } = this.appManager;

    if (loading) {
      return <RepoSpinner />;
    }
    if (threadPage === null && !loading) {
      return <FourOhFour type='Thread' subPage canBePrivate />;
    }

    const {
      membersOnly,
      canUpdate,
      canDelete,
      title,
      msg,
      category,
      creatorProfile,
      dateCreated,
      editing,
      tags,
      EditThread,
      Cancel,
      SaveThread,
      Delete,
      content,
      isPinned,
      SetPinned,
      canPin,
    } = threadPage;

    const { username } = creatorProfile;
    const { avatar } = creatorProfile;
    const { imageFallbackPath } = creatorProfile;
    const categoryName = category.title;

    const editor = new EditorStore({
      initialValue: markdown.deserialize(msg),
      formatting: 'markdown',
    });

    const categoryParams = Object.assign(
      {},
      clientStore.routerStore.route.params,
      { categoryId: category.urlId },
    );
    delete categoryParams.thread;

    const Info = (
      <ThreadPageInfo>
        {membersOnly ? <span>🔒</span> : null}
        {preview ? (
          <Trans>
            posted into{' '}
            <Link name='initiative.forum.category' params={categoryParams}>
              <CategoryName color={`#${category.color || 'cdcdcd'}`}>
                {{ categoryName }}
              </CategoryName>
            </Link>
          </Trans>
        ) : (
          <Trans>
            {' '}
            posted by{' '}
            <Link name='profile' params={{ profile: username }}>
              <UsernameWrapper>{{ username }}</UsernameWrapper>
            </Link>
          </Trans>
        )}
        {' | '}
        <Date value={dateCreated} />
        {canUpdate ? (
          editing ? (
            <span>
              {' | '}
              <DeleteBtn onClick={() => Cancel()}>
                <Trans>Cancel</Trans>
              </DeleteBtn>
              {' | '}
              <Edit onClick={() => SaveThread()}>
                <Trans>Save</Trans>
              </Edit>
            </span>
          ) : (
            <span>
              <span>
                {' | '}
                <Edit onClick={() => EditThread()}>
                  <Trans>Edit</Trans>
                </Edit>
              </span>
            </span>
          )
        ) : (
          ''
        )}
        {canDelete && !editing ? (
          <span>
            {' | '}
            <DeleteBtn
              onClick={() =>
                modalStore.SetModalComponent(ConfirmAction, {
                  callBack: Delete,
                  title: 'delete this Thread?',
                  undo: false,
                })
              }
            >
              <Trans>Delete</Trans>
            </DeleteBtn>
          </span>
        ) : (
          ''
        )}
      </ThreadPageInfo>
    );

    const threadFields = editing ? (
      <>
        <Div position='relative'>
          <TextField
            fieldStore={threadPage.form.fieldStores.get('title')}
            editComponent={TitleInput}
            validationPosition={{ top: '10px', right: '5px' }}
          />
        </Div>
        {Info}
        <SubPageDescription>
          <Div position='relative'>
            <EditorField
              fieldStore={threadPage.form.fieldStores.get('msg')}
              editComponent={MsgInput}
              paragraphSpacing='paragraphSpacing12'
              validationPosition={{ top: '10px', right: '5px' }}
              blueBorder
              noFormattingHint
            />
          </Div>
        </SubPageDescription>
      </>
    ) : (
      <>
        <SubPageTitle>{title}</SubPageTitle>
        {Info}
        {msg === '' && (
          <SubPageDescription>
            <i>
              <Trans>No message provided</Trans>.
            </i>
          </SubPageDescription>
        )}
        {!!msg && (
          <SubPageDescription>
            <Editor store={editor} paragraphSpacing='paragraphSpacing12' />
            <Like content={content} />
          </SubPageDescription>
        )}
      </>
    );

    if (preview) {
      return (
        <>
          <RowWrapper>
            <Link name='profile' params={{ profile: username }}>
              <AuthorAvatar
                src={imageSrc(
                  avatar,
                  imageFallbackPath,
                  mainConf.imgSizes.profile.large,
                )}
              />
            </Link>
            <ColumnWrapper>
              <FullName>{creatorProfile.fullName}</FullName>
              <Link name='profile' params={{ profile: username }}>
                <UsernameWrapper>{username}</UsernameWrapper>
              </Link>
            </ColumnWrapper>
            {canPin && (
              <ButtonStyled
                variant='secondary'
                icon={isPinned ? 'pinned' : 'unpinned'}
                onClick={() => SetPinned(!isPinned)}
                active={isPinned}
                outline
              />
            )}
          </RowWrapper>
          {threadFields}
          <Comments
            thread={preview}
            content={threadPage}
            contentType='thread'
          />
        </>
      );
    }

    const tagsSection =
      tags.length === 0 ? null : (
        <>
          <SubPageStatName first>
            <Trans>Topics</Trans>
          </SubPageStatName>
          {tags.map(tag => (
            <Link name='channel' params={{ channel: tag.name }} key={tag.id}>
              <Tag vertical>#{tag.name}</Tag>
            </Link>
          ))}
        </>
      );

    const {
      initiativeManager: { initiative },
    } = this.props;

    return (
      <>
        <DocumentHead title={`${title} - ${initiative.title} Forum`} />
        <Intro
          description={category.description}
          categoryName={category.title}
          categoryColor={`#${category.color}`}
          categoryUrlId={category.urlId}
        />
        <IssueWrapper>
          <SubPageWrapper>
            {threadFields}
            <Comments
              thread={preview}
              content={threadPage}
              contentType='thread'
            />
          </SubPageWrapper>
          <SubPageStatsWrapper>{tagsSection}</SubPageStatsWrapper>
        </IssueWrapper>
      </>
    );
  }
}

ThreadBody.propTypes = {
  threadPage: PropTypes.object,
  preview: PropTypes.bool,
  initiativeManager: PropTypes.object,
};
